/* eslint-disable no-shadow */

import Vue from 'vue';
/* eslint-disable import/no-cycle */
import ApiService from '@/services/api.service';
import { apiEndpoint } from '@/shared';
import { HttpError, PMSError, ValidationError } from '@/errors';

const endpoint = `${apiEndpoint}/channels`;
const connectivityEndpoint = `${apiEndpoint}/connectivity`;

const state = {
  data: null,
  mappingData: [],
  channelCredentialData: [],
  pending: false,
  pmsError: null,
  error: null,
  updatePending: false,
  updateError: null,
  mirrorRooms: [],
  ctypes: [],
  cplans: [],
  mapped: [],
  mirrorData: [],
  mappedProducts: [],
  updatingProduct: [],
};

const getters = {
  loaded: (state) => !state.pending && state.data != null,
  channel: (state) => (state.data ?? {}),
  ctypes: (state) => (state?.ctypes ?? []),
  cplans: (state) => (state?.cplans ?? []),
  rooms: (state) => (state?.mappingData?.rooms ?? []),
  plans: (state) => (state?.mappingData?.plans ?? []),
  mappedOrder: (state) => (state?.mappingData?.mappedOrder ?? []),
  mapped: (state) => (state.mapped ?? []),
  mirrorRoomsLoaded: (state) => state.mirrorRooms != null,
};

function updateMappedCount(state) {
  state.data.count = Array.isArray(state.mapped)
    ? state.mapped.length
    : Object.keys(state.mapped).length;
}

function updateChannelInList(commit, channel) {
  commit('channels/modified', { channel }, { root: true });
}

function updateChannelMappingsInList(commit, channel, rooms) {
  commit('channels/mappings', { channel, rooms }, { root: true });
}

const mutations = {
  clearErrors(state) {
    state.error = null;
    state.updateError = null;
  },
  beforeLoading(state, initial = false) {
    if (initial) {
      state.data = null;
    }
    state.error = null;
    state.pending = true;
    state.invalid = false;
    state.invalidMessage = '';
  },
  beforeUpdate(state) {
    state.updateError = null;
    state.updatePending = true;
  },
  afterUpdate(state, error = null) {
    state.updatePending = false;
    state.updateError = error;
  },
  data(state, { data = null, error = null, update = false } = {}) {
    if (!update) {
      state.pending = false;
    } else {
      state.updatePending = false;
    }
    state.data = data?.channel;
    state.cplans = data?.cplans;
    state.ctypes = data?.ctypes;
    state.mapped = data?.channel?.mapped;
    state.error = error;
    state.pmsError = error;
  },
  channelCredentialData(state, { data = null, error = null } = {}) {
    state.channelCredentialData = data.data;
    state.pending = false;
    state.error = error;
  },
  mappingData(state, { data = null, error = null, update = false } = {}) {
    if (!update) {
      state.pending = false;
    } else {
      state.updatePending = false;
    }
    state.mappingData = data;
    state.mapped = data?.mapped;
    state.error = error;
    state.pmsError = error;
  },
  connect(state, list = []) {
    if (Array.isArray(state.mapped)) Vue.set(state, 'mapped', {});
    list.forEach(({
      uniq, plan, mode, cplan: { id, typeid },
    }) => {
      Vue.set(state.mapped, plan.id, {
        id, typeid, uniq, mode,
      });
    });
    updateMappedCount(state);
    state.updatePending = false;
  },
  disconnect(state, id) {
    Vue.delete(state.mapped, id);
  },
  disconnectProducts(state, uniqId) {
    state.mappedProducts.forEach((product, index) => {
      const idx = product.findIndex((p) => p.uniqId === uniqId);
      Vue.delete(product, idx);

      if (product.length === 0) {
        Vue.delete(state.mappedProducts, index);
      }
    });
    state.updatePending = false;
  },
  connectionUpdate(state, list) {
    list.forEach((m) => {
      Vue.set(state.data.mapped, m.plan.id, m);
    });
    updateMappedCount(state);
    state.updatePending = false;
  },
  mappingsUpdated(state, { rooms = null, mappedOrderData = null, error = null } = {}) {
    state.updatePending = false;
    state.updateError = error;
    if (rooms != null) {
      state.mapped = rooms.filter(({ inv }) => inv).map(({ id }) => id);
    }
    if (mappedOrderData != null) {
      state.mappingData.mappedOrder = mappedOrderData;
    }
    updateMappedCount(state);
  },
  updatePending(state) {
    state.updatePending = false;
  },
  updateStatus(state, data) {
    state.data.status = data.status;
  },
  mirrorUpdate(state, data) {
    state.mirrorRooms = data.products ?? [];
  },
  mirrorDataUpdate(state, data) {
    state.mirrorData = data;
  },
  updateMappedProductsOpen(state, data) {
    state.mappedProducts[data.index][data.key].cultswitch.open = data.value;
  },
  updateMappedProductsMode(state, data) {
    state.mappedProducts[data.index][data.key].cultswitch.mode = data.value;
  },
  updateMappedProductsLoading(state, data) {
    state.mappedProducts[data.index][data.key].cultswitch.loading = data.value;
  },
  updateConnectedProducts(state, data) {
    state.mappedProducts = [];
    const groupedByCultuzzRoomId = new Map();

    data.products.forEach((product) => {
      const cultuzzRoomId = product.channel_product_details[0].room_id;

      if (!groupedByCultuzzRoomId.has(cultuzzRoomId)) {
        groupedByCultuzzRoomId.set(cultuzzRoomId, []);
      }

      groupedByCultuzzRoomId.get(cultuzzRoomId).push({
        uniqId: `${product.id}_${product.channel_product_details[0].rate_id}`,
        productId: product.id,
        productName: product.name,
        roomId: product.room.id,
        roomName: product.room.name,
        cultswitch: {
          rateId: product.channel_product_details[0].rate_id,
          rateName: product.channel_product_details[0].rate_name,
          roomId: product.channel_product_details[0].room_id,
          roomName: product.channel_product_details[0].room_name,
          mode: product.channel_product_details[0].extra_id,
          open: false,
          loading: false,
        },
      });
    });

    state.mappedProducts = Object.values(Object.fromEntries(groupedByCultuzzRoomId));

    state.updatePending = false;
  },
};

function handleApiError(commit, error, promo = false) {
  const { status, data } = error.response;
  let err = error;
  switch (status) {
    case 400: // BadRequest
      err = new HttpError(status, data.message);
      break;
    case 409: // PMSError
      err = new PMSError(status, data.message);
      break;
    case 422:
      if (promo) {
        const validationErrorMessage = [];
        // eslint-disable-next-line promise/prefer-await-to-callbacks
        Object.entries(data.errors).forEach((error) => {
          validationErrorMessage.push(error[1][0]);
        });
        data.message = validationErrorMessage;
      }
      err = new ValidationError(status, data.message, data.errors);
      break;
    case 500:
      err = new Error(data.message);
      break;
    default:
      break;
  }
  // commit('update', err);
  return err;
}

const actions = {
  async fetchData({ commit }, { id, forced = false, exceptExtras = false } = {}) {
    commit('beforeLoading', forced);
    try {
      const url = exceptExtras ? `${endpoint}/${id}?exceptExtras` : `${endpoint}/${id}`;
      const { data } = await ApiService.get(url);
      commit('data', { data });
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
  async fetchMappingData({ commit }, {
    id, forced = false, exceptExtras = 0, channelType = 'pull', onlyMapped = 0, withMappedOrder = 0,
  } = {}) {
    commit('beforeLoading', forced);
    try {
      const query = {
        exceptExtras,
        channelType,
        onlyMapped,
        withMappedOrder,
      };
      const queryParams = (new URLSearchParams(query)).toString();
      const url = `${endpoint}/mapping/${id}?${queryParams}`;
      const { data } = await ApiService.get(url);
      commit('mappingData', { data });
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
  async fetchChannelData({ commit }, { id, forced = false, channelType = 'pull' } = {}) {
    commit('beforeLoading', forced);
    try {
      const url = `${endpoint}/data/${id}?channelType=${channelType}`;
      const { data } = await ApiService.get(url);
      commit('mappingData', { data });
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
  async fetchChannelCredentialData({ commit }, id) {
    try {
      const { data } = await ApiService.get(`${endpoint}/channel-credentials/${id}`);
      commit('channelCredentialData', { data });
    } catch (error) {
      commit('channelCredentialData', { error });
      throw handleApiError(commit, error);
    }
  },
  async connectRatePlans({ commit, getters }, { id, list, update = false }) {
    commit('beforeUpdate');
    const rooms = list.map(({
      cplan: { id, name, typeid }, plan: { id: rid }, mode, ctype,
    }) => ({
      rid,
      id,
      name,
      typeid,
      mode,
      room_name: ctype,
      inv: true,
    }));

    try {
      await ApiService.put(`${endpoint}/${id}`, { rooms });
      if (!update) {
        commit('connect', list);
      } else {
        commit('connectionUpdate', list);
      }
      updateChannelInList(commit, getters.channel);
      updateChannelMappingsInList(commit, getters.channel, rooms);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async disconnectProduct({ commit }, { id, list, uniqId }) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${id}`, { rooms: list });
      commit('disconnectProducts', uniqId);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async updateProductConnection({ commit }, {
    id, room, index, key,
  }) {
    commit('updateMappedProductsLoading', { index, key, value: true });
    try {
      await ApiService.put(`${endpoint}/${id}`, { rooms: [room] });
      commit('updateMappedProductsMode', { index, key, value: room.mode });
      commit('updateMappedProductsLoading', { index, key, value: false });
      commit('updateMappedProductsOpen', { index, key, value: false });
    } catch (error) {
      commit('afterUpdate');
      commit('updateMappedProductsLoading', { index, key, value: false });
      commit('updateMappedProductsOpen', { index, key, value: false });
      throw handleApiError(commit, error);
    }
  },
  async channelMappings({ commit }, { id, rooms, mappedOrderData }) {
    commit('beforeUpdate');
    try {
      await ApiService.put(`${endpoint}/${id}`, { rooms, mappedOrderData });
      commit('mappingsUpdated', { rooms, mappedOrderData });
    } catch (error) {
      commit('mappingsUpdated', { error });
      throw handleApiError(commit, error);
    }
  },
  async updateRatePlansData({ commit }, { id, payload }) {
    commit('beforeUpdate');
    try {
      const sendPayload = {
        id,
        mode: 'update',
        ...payload,
      };
      await ApiService.patch(`${endpoint}/${id}/rate`, sendPayload);
      commit('updatePending');
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async updateChannelData({ commit, getters }, { id, payload }) {
    commit('beforeUpdate');
    try {
      const sendPayload = {
        mode: 'update',
        ...payload,
      };
      const { data } = await ApiService.patch(`${endpoint}/${id}`, sendPayload);
      commit('data', { data, update: true });
      updateChannelInList(commit, getters.channel);
    } catch (error) {
      // commit('mappingsUpdated', { error });
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async updateChannelStatus({ commit }, { channelId, channelMode }) {
    commit('beforeUpdate');
    try {
      const payload = {
        mode: channelMode,
      };
      const { data } = await ApiService.patch(`${endpoint}/${channelId}`, payload);
      commit('updatePending');
      commit('updateStatus', data);
    } catch (error) {
      commit('data', { error });
      throw handleApiError(commit, error);
    }
  },
  async getConnectedRatePlans({ commit }, payload) {
    try {
      const response = await ApiService.post(`${connectivityEndpoint}/mirror/connected-rate-plans`, payload);
      commit('mirrorUpdate', response.data);
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async getMirrorDownloadData({ commit }, payload, downloadable) {
    try {
      const config = downloadable ? {
        responseType: 'blob',
      } : {};
      return await ApiService.post(`${connectivityEndpoint}/mirror/download-data`, payload, config);
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
  async getConnectedProducts({ commit }, payload) {
    commit('beforeUpdate');
    try {
      const response = await ApiService.post(`${endpoint}/connected`, payload);
      commit('updateConnectedProducts', response.data);
    } catch (error) {
      commit('afterUpdate');
      throw handleApiError(commit, error);
    }
  },
  async getMirror({ commit }, payload) {
    /**
     *  product_id i.e. rate plan is optional,
     *  if it is not provided then details of all rate plans will be returned
     *  if the key is set, then it must also have a values
    */
    const payloadCopy = { ...payload };
    if (!payloadCopy.product_id) {
      delete payloadCopy.product_id;
    }

    try {
      const response = await ApiService.post(`${apiEndpoint}/calendar/channel-mirror`, payloadCopy);
      commit('mirrorDataUpdate', response.data);
    } catch (error) {
      commit('error', error);
      throw handleApiError(commit, error);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
